import { definePlatformSetting } from '@crest/platform-contracts/settings';
import { z } from 'zod';
export const trainingLearningConfigurationValueSchema = z.strictObject({
    maximumCourses: z.number().int().min(1).max(1_000),
    maximumLessonsPerRelease: z.number().int().min(1).max(100),
    maximumQuestionsPerAssessment: z.number().int().min(1).max(100),
    maximumAttemptsPerAssessment: z.number().int().min(1).max(20),
    defaultPassingScoreBasisPoints: z.number().int().min(1).max(10_000),
    defaultAssignmentDueDays: z.number().int().min(1).max(365),
});
export const trainingLearningConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'training.learning.configuration', moduleId: 'training',
        title: 'Training learning behavior',
        helpText: 'Controls tenant course limits, assessment bounds, scoring defaults, and assignment due dates.',
        configurationDomain: 'module', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            maximumCourses: 500, maximumLessonsPerRelease: 100,
            maximumQuestionsPerAssessment: 100, maximumAttemptsPerAssessment: 20,
            defaultPassingScoreBasisPoints: 8_000, defaultAssignmentDueDays: 30,
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'ordinary',
    },
    valueSchema: trainingLearningConfigurationValueSchema,
});
export const trainingActivityConfigurationValueSchema = z.strictObject({
    heartbeatCreditSeconds: z.union([z.literal(15), z.literal(30), z.literal(45), z.literal(60)]),
    maximumSessionMinutes: z.union([z.literal(120), z.literal(240), z.literal(480)]),
    managerReportPageSize: z.union([z.literal(25), z.literal(50), z.literal(100), z.literal(200)]),
});
export const trainingActivityConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'training.activity.configuration', moduleId: 'training',
        title: 'Training activity behavior',
        helpText: 'Controls bounded heartbeat credit, maximum session duration, and manager report size.',
        configurationDomain: 'module', valueType: 'object', schemaVersion: 1,
        codedFallback: { heartbeatCreditSeconds: 30, maximumSessionMinutes: 480, managerReportPageSize: 100 },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'ordinary',
    },
    valueSchema: trainingActivityConfigurationValueSchema,
});
export const trainingCertificationConfigurationValueSchema = z.strictObject({
    expiryWarningDays: z.number().int().min(1).max(365),
    evidenceRequired: z.boolean(),
    credentialNumberRequired: z.boolean(),
    allowNonExpiring: z.boolean(),
    maximumCurrentRecordsPerMembership: z.number().int().min(1).max(500),
});
export const trainingCertificationConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'training.certifications.configuration', moduleId: 'training',
        title: 'Professional certification behavior',
        helpText: 'Controls expiry warnings, required tenant-recorded evidence, and per-member record bounds without claiming issuer verification.',
        configurationDomain: 'module', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            expiryWarningDays: 60, evidenceRequired: true, credentialNumberRequired: false,
            allowNonExpiring: true, maximumCurrentRecordsPerMembership: 200,
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'ordinary',
    },
    valueSchema: trainingCertificationConfigurationValueSchema,
});
export const trainingCenterDisplayValueSchema = z.strictObject({
    layout: z.enum(['grid', 'list']),
    cardSize: z.enum(['compact', 'standard', 'large']),
    density: z.enum(['comfortable', 'compact']),
    showDuration: z.boolean(),
    showProgressPercentage: z.boolean(),
    showCategory: z.boolean(),
});
export const trainingCenterDisplaySetting = definePlatformSetting({
    definition: {
        key: 'training.display.center', moduleId: 'training', title: 'Training Center display',
        helpText: 'Controls course layout, card size, density, and visible course metadata.',
        configurationDomain: 'layout', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            layout: 'grid', cardSize: 'standard', density: 'comfortable',
            showDuration: true, showProgressPercentage: true, showCategory: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: true, offlineEligible: true, safetyClass: 'ordinary',
    },
    valueSchema: trainingCenterDisplayValueSchema,
});
export const trainingManagementDisplayValueSchema = z.strictObject({
    editorLayout: z.enum(['stacked', 'split-preview']),
    sectionDensity: z.enum(['comfortable', 'compact']),
    previewDefaultOpen: z.boolean(),
    showPayloadPreview: z.boolean(),
});
export const trainingManagementDisplaySetting = definePlatformSetting({
    definition: {
        key: 'training.display.management', moduleId: 'training', title: 'Training authoring display',
        helpText: 'Controls the tenant Training release editor layout, density, preview, and read-only payload view.',
        configurationDomain: 'layout', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            editorLayout: 'stacked', sectionDensity: 'comfortable',
            previewDefaultOpen: true, showPayloadPreview: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: true, offlineEligible: false, safetyClass: 'ordinary',
    },
    valueSchema: trainingManagementDisplayValueSchema,
});
export const trainingTimesheetSummaryDisplayValueSchema = z.strictObject({
    enabled: z.boolean(),
    initiallyExpanded: z.boolean(),
    density: z.enum(['comfortable', 'compact']),
    desktopListSize: z.enum(['small', 'standard', 'large']),
    showExpectedDuration: z.boolean(),
    showInteractions: z.boolean(),
    showLifecycle: z.boolean(),
    sessionLimit: z.number().int().min(5).max(100),
});
export const trainingTimesheetSummaryDisplaySetting = definePlatformSetting({
    definition: {
        key: 'training.display.timesheet-summary',
        moduleId: 'training',
        title: 'Training summary in Timesheets',
        helpText: 'Controls the removable Training activity panel layout, disclosure, visible metrics, and bounded session count.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            enabled: true,
            initiallyExpanded: false,
            density: 'comfortable',
            desktopListSize: 'standard',
            showExpectedDuration: true,
            showInteractions: true,
            showLifecycle: true,
            sessionLimit: 50,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: false,
        safetyClass: 'ordinary',
    },
    valueSchema: trainingTimesheetSummaryDisplayValueSchema,
});
export const trainingWorkforceProfileStatusDisplayValueSchema = z.strictObject({
    enabled: z.boolean(),
    initiallyExpanded: z.boolean(),
    density: z.enum(['comfortable', 'compact']),
    showAssignmentBreakdown: z.boolean(),
    showCertificationBreakdown: z.boolean(),
    showDates: z.boolean(),
});
export const trainingWorkforceProfileStatusDisplaySetting = definePlatformSetting({
    definition: {
        key: 'training.display.workforce-profile-status',
        moduleId: 'training',
        title: 'Training status in Workforce',
        helpText: 'Controls the removable Training status panel disclosure, density, breakdowns, and dates in the Workforce employee profile host.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            enabled: true,
            initiallyExpanded: true,
            density: 'comfortable',
            showAssignmentBreakdown: true,
            showCertificationBreakdown: true,
            showDates: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: false,
        safetyClass: 'ordinary',
    },
    valueSchema: trainingWorkforceProfileStatusDisplayValueSchema,
});
const trainingStatusLabelsSchema = z.strictObject({
    draft: z.string().trim().min(1).max(60), published: z.string().trim().min(1).max(60),
    archived: z.string().trim().min(1).max(60), assigned: z.string().trim().min(1).max(60),
    inProgress: z.string().trim().min(1).max(60), completed: z.string().trim().min(1).max(60),
    waived: z.string().trim().min(1).max(60),
});
export const trainingLearningCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    centerTitle: z.string().trim().min(1).max(100), centerDescription: z.string().trim().min(1).max(300),
    progressLabel: z.string().trim().min(1).max(80), durationLabel: z.string().trim().min(1).max(80),
    lessonsLabel: z.string().trim().min(1).max(80), citationsLabel: z.string().trim().min(1).max(80),
    openCourseAction: z.string().trim().min(1).max(80), completeLessonAction: z.string().trim().min(1).max(80),
    assessmentAction: z.string().trim().min(1).max(80), startAssessmentAction: z.string().trim().min(1).max(80),
    submitAssessmentAction: z.string().trim().min(1).max(80), scoreLabel: z.string().trim().min(1).max(80),
    passedLabel: z.string().trim().min(1).max(80), notPassedLabel: z.string().trim().min(1).max(80),
    internalEvidenceLabel: z.string().trim().min(1).max(200),
    knowledgeReferencesLabel: z.string().trim().min(1).max(100),
    openKnowledgeReferenceAction: z.string().trim().min(1).max(100),
    retainedKnowledgeReferenceLabel: z.string().trim().min(1).max(160),
    loadingLabel: z.string().trim().min(1).max(120), emptyTitle: z.string().trim().min(1).max(120),
    emptyDescription: z.string().trim().min(1).max(300), loadError: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240), statusLabels: trainingStatusLabelsSchema,
});
export const trainingLearningCopySetting = definePlatformSetting({
    definition: {
        key: 'training.copy.learning', moduleId: 'training', title: 'Training learner copy',
        helpText: 'Controls tenant-editable Training Center, lesson, assessment, progress, and status labels.',
        configurationDomain: 'copy', valueType: 'object', schemaVersion: 2,
        codedFallback: {
            navigationLabel: 'Training', centerTitle: 'Training Center',
            centerDescription: 'Assigned learning, progress, and internal completion evidence.',
            progressLabel: 'Progress', durationLabel: 'Duration', lessonsLabel: 'Lessons', citationsLabel: 'Sources',
            openCourseAction: 'Open course', completeLessonAction: 'Mark lesson complete',
            assessmentAction: 'Assessment', startAssessmentAction: 'Start assessment',
            submitAssessmentAction: 'Submit assessment', scoreLabel: 'Score', passedLabel: 'Passed',
            notPassedLabel: 'Not passed', internalEvidenceLabel: 'Internal Training evidence only',
            knowledgeReferencesLabel: 'Current policies and procedures',
            openKnowledgeReferenceAction: 'Open in Knowledge',
            retainedKnowledgeReferenceLabel: 'Exact published reference retained with this Training release',
            loadingLabel: 'Loading Training…', emptyTitle: 'No Training courses available',
            emptyDescription: 'Published or assigned Training courses will appear here.',
            loadError: 'Training could not be loaded.', mutationError: 'The Training change could not be saved.',
            statusLabels: {
                draft: 'Draft', published: 'Published', archived: 'Archived', assigned: 'Assigned',
                inProgress: 'In progress', completed: 'Completed', waived: 'Waived',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: true, safetyClass: 'ordinary',
    },
    valueSchema: trainingLearningCopyValueSchema,
    migrations: [{
            fromVersion: 1,
            toVersion: 2,
            migrate: (value) => ({
                ...(typeof value === 'object' && value !== null && !Array.isArray(value) ? value : {}),
                knowledgeReferencesLabel: 'Current policies and procedures',
                openKnowledgeReferenceAction: 'Open in Knowledge',
                retainedKnowledgeReferenceLabel: 'Exact published reference retained with this Training release',
            }),
        }],
});
const trainingManagementAuthoringCopySchema = z.strictObject({
    description: z.string().trim().min(1).max(300),
    immutableReleaseNotice: z.string().trim().min(1).max(300),
    loadingLabel: z.string().trim().min(1).max(120),
    mutationError: z.string().trim().min(1).max(240),
    validationTitle: z.string().trim().min(1).max(120),
    courseListLabel: z.string().trim().min(1).max(100),
    editorTitle: z.string().trim().min(1).max(120),
    releaseLifecycleTitle: z.string().trim().min(1).max(120),
    rollbackReleaseLabel: z.string().trim().min(1).max(100),
    tenantOriginLabel: z.string().trim().min(1).max(80),
    platformOriginLabel: z.string().trim().min(1).max(80),
    metadataTitle: z.string().trim().min(1).max(120),
    titleLabel: z.string().trim().min(1).max(80),
    descriptionLabel: z.string().trim().min(1).max(80),
    categoryLabel: z.string().trim().min(1).max(80),
    difficultyLabel: z.string().trim().min(1).max(80),
    estimatedDurationLabel: z.string().trim().min(1).max(80),
    mandatoryLabel: z.string().trim().min(1).max(120),
    prerequisitesLabel: z.string().trim().min(1).max(120),
    completionNoticeLabel: z.string().trim().min(1).max(120),
    lessonsTitle: z.string().trim().min(1).max(120),
    addLessonAction: z.string().trim().min(1).max(80),
    keyLabel: z.string().trim().min(1).max(80),
    summaryLabel: z.string().trim().min(1).max(80),
    bodyLabel: z.string().trim().min(1).max(80),
    lessonDurationLabel: z.string().trim().min(1).max(80),
    citationReferencesLabel: z.string().trim().min(1).max(100),
    noCitationsLabel: z.string().trim().min(1).max(120),
    citationsTitle: z.string().trim().min(1).max(120),
    addCitationAction: z.string().trim().min(1).max(80),
    sourceTitleLabel: z.string().trim().min(1).max(80),
    sourceUrlLabel: z.string().trim().min(1).max(80),
    locatorLabel: z.string().trim().min(1).max(80),
    excerptLabel: z.string().trim().min(1).max(80),
    knowledgeDocumentIdLabel: z.string().trim().min(1).max(100),
    knowledgeReferenceCurrentLabel: z.string().trim().min(1).max(160),
    assessmentTitle: z.string().trim().min(1).max(120),
    assessmentEnabledLabel: z.string().trim().min(1).max(120),
    passingScoreLabel: z.string().trim().min(1).max(100),
    attemptsAllowedLabel: z.string().trim().min(1).max(100),
    questionCountLabel: z.string().trim().min(1).max(100),
    questionsTitle: z.string().trim().min(1).max(120),
    addQuestionAction: z.string().trim().min(1).max(80),
    linkedLessonLabel: z.string().trim().min(1).max(100),
    noLinkedLessonLabel: z.string().trim().min(1).max(100),
    promptLabel: z.string().trim().min(1).max(80),
    explanationLabel: z.string().trim().min(1).max(80),
    optionsTitle: z.string().trim().min(1).max(120),
    addOptionAction: z.string().trim().min(1).max(80),
    optionTextLabel: z.string().trim().min(1).max(80),
    correctOptionLabel: z.string().trim().min(1).max(100),
    previewTitle: z.string().trim().min(1).max(120),
    showPreviewAction: z.string().trim().min(1).max(80),
    hidePreviewAction: z.string().trim().min(1).max(80),
    payloadPreviewTitle: z.string().trim().min(1).max(120),
    moveUpAction: z.string().trim().min(1).max(80),
    moveDownAction: z.string().trim().min(1).max(80),
    removeAction: z.string().trim().min(1).max(80),
    difficultyLabels: z.strictObject({
        beginner: z.string().trim().min(1).max(60),
        intermediate: z.string().trim().min(1).max(60),
        advanced: z.string().trim().min(1).max(60),
    }),
    statusLabels: trainingStatusLabelsSchema,
});
const trainingManagementAuthoringCopyFallback = {
    description: 'Build tenant-owned Training courses with structured, versioned release controls.',
    immutableReleaseNotice: 'Every save creates an immutable release so earlier learner evidence and rollback history remain intact.',
    loadingLabel: 'Loading Training management…',
    mutationError: 'The Training management change could not be completed.',
    validationTitle: 'Review these release fields before saving',
    courseListLabel: 'Training courses', editorTitle: 'Release editor',
    releaseLifecycleTitle: 'Release lifecycle', rollbackReleaseLabel: 'Rollback release',
    tenantOriginLabel: 'Tenant release', platformOriginLabel: 'Platform release',
    metadataTitle: 'Course metadata', titleLabel: 'Course title', descriptionLabel: 'Course description',
    categoryLabel: 'Category', difficultyLabel: 'Difficulty',
    estimatedDurationLabel: 'Estimated duration in minutes',
    mandatoryLabel: 'Assign by default when the course is published',
    prerequisitesLabel: 'Prerequisite course slugs, one per line',
    completionNoticeLabel: 'Internal completion notice', lessonsTitle: 'Ordered lessons',
    addLessonAction: 'Add lesson', keyLabel: 'Stable key', summaryLabel: 'Summary',
    bodyLabel: 'Lesson content', lessonDurationLabel: 'Duration in minutes',
    citationReferencesLabel: 'Referenced sources', noCitationsLabel: 'Add a source to cite it here.',
    citationsTitle: 'Sources and citations', addCitationAction: 'Add source',
    sourceTitleLabel: 'Source title', sourceUrlLabel: 'Source URL (optional)', locatorLabel: 'Locator',
    excerptLabel: 'Rights-safe excerpt or description', assessmentTitle: 'Assessment',
    knowledgeDocumentIdLabel: 'Knowledge document ID (optional)',
    knowledgeReferenceCurrentLabel: 'Current published Knowledge version retained on save',
    assessmentEnabledLabel: 'Include an assessment', passingScoreLabel: 'Passing score percent',
    attemptsAllowedLabel: 'Attempts allowed', questionCountLabel: 'Questions presented per attempt',
    questionsTitle: 'Ordered question bank', addQuestionAction: 'Add question',
    linkedLessonLabel: 'Linked lesson', noLinkedLessonLabel: 'No linked lesson',
    promptLabel: 'Question prompt', explanationLabel: 'Answer explanation',
    optionsTitle: 'Ordered response options', addOptionAction: 'Add option',
    optionTextLabel: 'Response text', correctOptionLabel: 'Correct response',
    previewTitle: 'Release preview', showPreviewAction: 'Show preview', hidePreviewAction: 'Hide preview',
    payloadPreviewTitle: 'Read-only release payload', moveUpAction: 'Move up',
    moveDownAction: 'Move down', removeAction: 'Remove',
    difficultyLabels: { beginner: 'Beginner', intermediate: 'Intermediate', advanced: 'Advanced' },
    statusLabels: {
        draft: 'Draft', published: 'Published', archived: 'Archived', assigned: 'Assigned',
        inProgress: 'In progress', completed: 'Completed', waived: 'Waived',
    },
};
export const trainingManagementCopyValueSchema = z.strictObject({
    managementNavigationLabel: z.string().trim().min(1).max(60), gradesNavigationLabel: z.string().trim().min(1).max(60),
    contentTitle: z.string().trim().min(1).max(100), assignmentTitle: z.string().trim().min(1).max(100),
    gradesTitle: z.string().trim().min(1).max(100), activityTitle: z.string().trim().min(1).max(100),
    createCourseAction: z.string().trim().min(1).max(80), appendReleaseAction: z.string().trim().min(1).max(80),
    publishAction: z.string().trim().min(1).max(80), rollbackAction: z.string().trim().min(1).max(80),
    archiveAction: z.string().trim().min(1).max(80), assignAction: z.string().trim().min(1).max(80),
    waiveAction: z.string().trim().min(1).max(80), slugLabel: z.string().trim().min(1).max(80),
    originLabel: z.string().trim().min(1).max(80), releaseJsonLabel: z.string().trim().min(1).max(80),
    reasonLabel: z.string().trim().min(1).max(80), membershipLabel: z.string().trim().min(1).max(80),
    dueAtLabel: z.string().trim().min(1).max(80), attemptLabel: z.string().trim().min(1).max(80),
    activityPrivacyNotice: z.string().trim().min(20).max(300), savingLabel: z.string().trim().min(1).max(120),
    authoring: trainingManagementAuthoringCopySchema,
});
export const trainingManagementCopySetting = definePlatformSetting({
    definition: {
        key: 'training.copy.management', moduleId: 'training', title: 'Training management copy',
        helpText: 'Controls tenant-editable content, assignment, grade, and private-activity administration labels.',
        configurationDomain: 'copy', valueType: 'object', schemaVersion: 3,
        codedFallback: {
            managementNavigationLabel: 'Training management', gradesNavigationLabel: 'Training grades',
            contentTitle: 'Training content releases', assignmentTitle: 'Training assignments',
            gradesTitle: 'Training grades', activityTitle: 'Private Training activity',
            createCourseAction: 'Create course', appendReleaseAction: 'Add release', publishAction: 'Publish release',
            rollbackAction: 'Roll back by new release', archiveAction: 'Archive course', assignAction: 'Assign course',
            waiveAction: 'Waive assignment', slugLabel: 'Course slug', originLabel: 'Content origin',
            releaseJsonLabel: 'Validated Training release JSON', reasonLabel: 'Reason', membershipLabel: 'Membership',
            dueAtLabel: 'Due date', attemptLabel: 'Attempt',
            activityPrivacyNotice: 'Private Training activity is restricted manager evidence and is not a timecard or payroll fact.',
            savingLabel: 'Saving Training…',
            authoring: trainingManagementAuthoringCopyFallback,
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'ordinary',
    },
    valueSchema: trainingManagementCopyValueSchema,
    migrations: [{
            fromVersion: 1,
            toVersion: 2,
            migrate: (value) => ({
                ...(typeof value === 'object' && value !== null && !Array.isArray(value) ? value : {}),
                authoring: trainingManagementAuthoringCopyFallback,
            }),
        }, {
            fromVersion: 2,
            toVersion: 3,
            migrate: (value) => {
                const record = typeof value === 'object' && value !== null && !Array.isArray(value)
                    ? value
                    : {};
                const authoring = typeof record.authoring === 'object' && record.authoring !== null &&
                    !Array.isArray(record.authoring)
                    ? record.authoring
                    : {};
                return {
                    ...record,
                    authoring: {
                        ...trainingManagementAuthoringCopyFallback,
                        ...authoring,
                        knowledgeDocumentIdLabel: 'Knowledge document ID (optional)',
                        knowledgeReferenceCurrentLabel: 'Current published Knowledge version retained on save',
                    },
                };
            },
        }],
});
export const trainingCertificationCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    pageTitle: z.string().trim().min(1).max(100),
    pageDescription: z.string().trim().min(1).max(300),
    supplementalNotice: z.string().trim().min(20).max(500),
    myRecordsLabel: z.string().trim().min(1).max(80),
    allRecordsLabel: z.string().trim().min(1).max(80),
    recordAction: z.string().trim().min(1).max(80),
    renewAction: z.string().trim().min(1).max(80),
    revokeAction: z.string().trim().min(1).max(80),
    evidenceLabel: z.string().trim().min(1).max(100),
    emptyTitle: z.string().trim().min(1).max(120),
    emptyDescription: z.string().trim().min(1).max(300),
    statusLabels: z.strictObject({
        active: z.string().trim().min(1).max(60),
        expiring: z.string().trim().min(1).max(60),
        expired: z.string().trim().min(1).max(60),
        nonExpiring: z.string().trim().min(1).max(60),
        superseded: z.string().trim().min(1).max(60),
        revoked: z.string().trim().min(1).max(60),
    }),
});
export const trainingCertificationCopySetting = definePlatformSetting({
    definition: {
        key: 'training.copy.certifications', moduleId: 'training',
        title: 'Professional certification copy',
        helpText: 'Controls tenant-editable professional credential labels while preserving the required issuer-verification disclaimer.',
        configurationDomain: 'copy', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Professional certifications', pageTitle: 'Professional certifications',
            pageDescription: 'Tenant-recorded external credentials, expiry dates, evidence references, and retained renewal history.',
            supplementalNotice: 'Follow your tenant credential-review procedure before assigning work that requires outside authority.',
            myRecordsLabel: 'My certifications', allRecordsLabel: 'All certifications',
            recordAction: 'Record certification', renewAction: 'Record renewal', revokeAction: 'Revoke record',
            evidenceLabel: 'Evidence reference and SHA-256', emptyTitle: 'No professional certifications recorded',
            emptyDescription: 'Authorized managers can record tenant-attested external credential evidence here.',
            statusLabels: {
                active: 'Active', expiring: 'Expiring', expired: 'Expired', nonExpiring: 'No expiration',
                superseded: 'Superseded', revoked: 'Revoked',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'protected',
    },
    valueSchema: trainingCertificationCopyValueSchema,
});
export const trainingTimesheetSummaryCopyValueSchema = z.strictObject({
    title: z.string().trim().min(1).max(120),
    supportingNotice: z.string().trim().min(20).max(360),
    reviewAction: z.string().trim().min(1).max(80),
    hideAction: z.string().trim().min(1).max(80),
    selectedEmployeeLabel: z.string().trim().min(1).max(100),
    visitLabel: z.string().trim().min(1).max(60),
    visitsLabel: z.string().trim().min(1).max(60),
    totalLabel: z.string().trim().min(1).max(60),
    totalPageOpenLabel: z.string().trim().min(1).max(100),
    totalForegroundLabel: z.string().trim().min(1).max(100),
    totalInteractionsLabel: z.string().trim().min(1).max(100),
    pageOpenLabel: z.string().trim().min(1).max(80),
    foregroundLabel: z.string().trim().min(1).max(80),
    interactionsLabel: z.string().trim().min(1).max(80),
    expectedLabel: z.string().trim().min(1).max(80),
    comparisonSuffix: z.string().trim().min(1).max(80),
    lowForegroundLabel: z.string().trim().min(1).max(120),
    loadingLabel: z.string().trim().min(1).max(160),
    errorLabel: z.string().trim().min(1).max(240),
    retryAction: z.string().trim().min(1).max(80),
    emptyLabel: z.string().trim().min(1).max(240),
    showingLabel: z.string().trim().min(1).max(80),
    ofLabel: z.string().trim().min(1).max(40),
    lifecycleLabels: z.strictObject({
        live: z.string().trim().min(1).max(80),
        noRecentHeartbeat: z.string().trim().min(1).max(80),
        interrupted: z.string().trim().min(1).max(80),
        closed: z.string().trim().min(1).max(80),
    }),
    durationUnits: z.strictObject({
        seconds: z.string().trim().min(1).max(20),
        minutes: z.string().trim().min(1).max(20),
        hours: z.string().trim().min(1).max(20),
    }),
});
export const trainingTimesheetSummaryCopySetting = definePlatformSetting({
    definition: {
        key: 'training.copy.timesheet-summary',
        moduleId: 'training',
        title: 'Training Timesheets-summary copy',
        helpText: 'Controls every tenant-editable label in the removable Training activity summary shown by a Timesheets host.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            title: 'Training activity',
            supportingNotice: 'Supporting Training page activity only—not a timecard, payable duration, or extra paid entry.',
            reviewAction: 'Review activity',
            hideAction: 'Hide activity',
            selectedEmployeeLabel: 'Selected employee',
            visitLabel: 'page visit',
            visitsLabel: 'page visits',
            totalLabel: 'total',
            totalPageOpenLabel: 'Page open',
            totalForegroundLabel: 'In foreground',
            totalInteractionsLabel: 'Interactions',
            pageOpenLabel: 'Page open',
            foregroundLabel: 'In foreground',
            interactionsLabel: 'Interactions',
            expectedLabel: 'Expected',
            comparisonSuffix: '× expected',
            lowForegroundLabel: 'Low foreground activity',
            loadingLabel: 'Loading Training activity…',
            errorLabel: 'Training activity is unavailable. Timesheets data was not changed.',
            retryAction: 'Try again',
            emptyLabel: 'No measured Training activity exists for this employee and window.',
            showingLabel: 'Showing',
            ofLabel: 'of',
            lifecycleLabels: {
                live: 'Live',
                noRecentHeartbeat: 'No recent heartbeat',
                interrupted: 'Interrupted',
                closed: 'Closed',
            },
            durationUnits: { seconds: 'sec', minutes: 'min', hours: 'hr' },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: false,
        safetyClass: 'ordinary',
    },
    valueSchema: trainingTimesheetSummaryCopyValueSchema,
});
export const trainingWorkforceProfileStatusCopyValueSchema = z.strictObject({
    title: z.string().trim().min(1).max(100),
    supportingNotice: z.string().trim().min(20).max(500),
    reviewAction: z.string().trim().min(1).max(80),
    hideAction: z.string().trim().min(1).max(80),
    loadingLabel: z.string().trim().min(1).max(160),
    errorLabel: z.string().trim().min(1).max(240),
    retryAction: z.string().trim().min(1).max(80),
    assignmentsLabel: z.string().trim().min(1).max(80),
    assignedLabel: z.string().trim().min(1).max(80),
    inProgressLabel: z.string().trim().min(1).max(80),
    completedAssignmentsLabel: z.string().trim().min(1).max(80),
    waivedLabel: z.string().trim().min(1).max(80),
    overdueLabel: z.string().trim().min(1).max(80),
    nearestDueLabel: z.string().trim().min(1).max(80),
    completedCoursesLabel: z.string().trim().min(1).max(80),
    latestCompletionLabel: z.string().trim().min(1).max(80),
    certificationsLabel: z.string().trim().min(1).max(80),
    activeLabel: z.string().trim().min(1).max(80),
    expiringLabel: z.string().trim().min(1).max(80),
    expiredLabel: z.string().trim().min(1).max(80),
    nonExpiringLabel: z.string().trim().min(1).max(80),
    nearestExpiryLabel: z.string().trim().min(1).max(80),
    noDateLabel: z.string().trim().min(1).max(80),
    certificationNotice: z.string().trim().min(20).max(500),
});
export const trainingWorkforceProfileStatusCopySetting = definePlatformSetting({
    definition: {
        key: 'training.copy.workforce-profile-status',
        moduleId: 'training',
        title: 'Training Workforce-profile status copy',
        helpText: 'Controls every tenant-editable label in the removable Training status panel shown by a Workforce employee-profile host.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            title: 'Training status',
            supportingNotice: 'A minimized manager view of Training-owned assignments, course completions, and professional credential status.',
            reviewAction: 'Review Training status',
            hideAction: 'Hide Training status',
            loadingLabel: 'Loading Training status…',
            errorLabel: 'Training status is unavailable. The Workforce profile was not changed.',
            retryAction: 'Try again',
            assignmentsLabel: 'Assignments',
            assignedLabel: 'Assigned',
            inProgressLabel: 'In progress',
            completedAssignmentsLabel: 'Completed assignments',
            waivedLabel: 'Waived',
            overdueLabel: 'Overdue',
            nearestDueLabel: 'Nearest due date',
            completedCoursesLabel: 'Completed courses',
            latestCompletionLabel: 'Latest completion',
            certificationsLabel: 'Current professional credentials',
            activeLabel: 'Active',
            expiringLabel: 'Expiring',
            expiredLabel: 'Expired',
            nonExpiringLabel: 'Non-expiring',
            nearestExpiryLabel: 'Nearest expiry',
            noDateLabel: 'None recorded',
            certificationNotice: 'Professional credentials are tenant-attested records. CREST does not verify or confer outside issuer authority.',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: false,
        safetyClass: 'ordinary',
    },
    valueSchema: trainingWorkforceProfileStatusCopyValueSchema,
});
export const trainingSettingRegistrations = [
    trainingLearningConfigurationSetting,
    trainingActivityConfigurationSetting,
    trainingCertificationConfigurationSetting,
    trainingCenterDisplaySetting,
    trainingManagementDisplaySetting,
    trainingTimesheetSummaryDisplaySetting,
    trainingWorkforceProfileStatusDisplaySetting,
    trainingLearningCopySetting,
    trainingManagementCopySetting,
    trainingCertificationCopySetting,
    trainingTimesheetSummaryCopySetting,
    trainingWorkforceProfileStatusCopySetting,
];
//# sourceMappingURL=training-setting-definitions.js.map